import FormattedList from 'I18n/components/FormattedList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import unescapedText from 'I18n/utils/unescapedText';
import UILink from 'UIComponents/link/UILink';
import UIDescriptionList from 'UIComponents/list/UIDescriptionList';
import UITag from 'UIComponents/tag/UITag';
import { Fragment, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import { getCategoryFeatureI18nKey } from 'marketplace-ui-apps-core/common/utils/i18n';
import { getCategoryI18nKey } from 'marketplace-ui-common/utils/i18n';
import { formatInstalls } from 'marketplace-ui-common/utils/installs';
import { CATEGORY_FEATURES } from '../../../../constants/search/FilterKey';
import { getCategoryV2Url, getFormattedUrl } from '../../../../utils/urls';

// @ts-expect-error untyped dependency
import localeComparator from 'I18n/utils/localeComparator';
// @ts-expect-error untyped dependency
import { reportMissingCategoryFeatureTranslation } from '../../../../utils/sentry';
// @ts-expect-error untyped dependency
import FilterTag from '../FilterTag';
// @ts-expect-error untyped dependency
import { getIsUngatedForRecategorization } from '../../../../selectors/auth';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Section = ({
  children,
  label,
  labelClassName
}) => /*#__PURE__*/_jsxs(Fragment, {
  children: [/*#__PURE__*/_jsx("dt", {
    className: labelClassName,
    children: label
  }), /*#__PURE__*/_jsx("dd", {
    children: children
  })]
});
export function AppDetails({
  appLanguages,
  category,
  categoryV2,
  companyName,
  companyUrl,
  categoryFeatures,
  getFilterLink,
  homemade,
  installs,
  isPreview,
  onTrackInteraction
}) {
  const isUngatedForAppRecategorization = useSelector(getIsUngatedForRecategorization);
  const trackInteraction = useCallback((action, value) => onTrackInteraction(action, {
    section: 'details',
    value
  }), [onTrackInteraction]);
  const renderedCategorySection = useMemo(() => {
    return category ? /*#__PURE__*/_jsx(Section, {
      label: unescapedText('appMarketplace.detail.overview.sidebar.details.categoryLabel'),
      labelClassName: "m-bottom-1",
      children: /*#__PURE__*/_jsx(UIRouterLink, {
        disabled: isPreview,
        to: getFilterLink(),
        children: /*#__PURE__*/_jsx(FilterTag, {
          children: /*#__PURE__*/_jsx(UITag, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getCategoryI18nKey(category)
            })
          })
        })
      })
    }) : null;
  }, [category, getFilterLink, isPreview]);
  const renderedCategoryV2Section = useMemo(() => {
    return categoryV2 && categoryV2.length > 0 ? /*#__PURE__*/_jsx(Section, {
      label: unescapedText('appMarketplace.detail.overview.sidebar.details.categoryV2Label'),
      labelClassName: "m-bottom-1",
      children: categoryV2.map((categoryItem, index) => /*#__PURE__*/_jsx(UIRouterLink, {
        className: "m-right-2",
        disabled: isPreview,
        to: getCategoryV2Url(categoryItem),
        children: /*#__PURE__*/_jsx(FilterTag, {
          children: /*#__PURE__*/_jsx(UITag, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `marketplace-ui-apps-core.groupTags.category.${categoryItem}`
            })
          })
        })
      }, index))
    }) : null;
  }, [categoryV2, isPreview]);
  const renderedFeaturesSection = useMemo(() => {
    return category && categoryFeatures.length ? /*#__PURE__*/_jsx(Section, {
      label: unescapedText('appMarketplace.detail.overview.sidebar.details.featuresLabel'),
      labelClassName: "m-bottom-1",
      children: categoryFeatures.map(feature => [feature, getCategoryFeatureI18nKey(category, feature)]).filter(([feature, featureI18nKey]) => {
        if (!isValidI18nKey(featureI18nKey)) {
          reportMissingCategoryFeatureTranslation(category, feature, {
            component: 'AppDetail'
          });
          return false;
        }
        return true;
      }).sort(([__, featureAI18nKey], [___, featureBI18nKey]) => localeComparator(unescapedText(featureBI18nKey), unescapedText(featureAI18nKey))).map(([feature, featureI18nKey]) => /*#__PURE__*/_jsx(UIRouterLink, {
        disabled: isPreview,
        to: getFilterLink(CATEGORY_FEATURES, feature),
        children: /*#__PURE__*/_jsx(FilterTag, {
          children: /*#__PURE__*/_jsx(UITag, {
            className: "m-left-0 m-right-1",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: featureI18nKey
            })
          })
        })
      }, feature))
    }) : null;
  }, [category, categoryFeatures, getFilterLink, isPreview]);
  const renderedLanguagesSection = useMemo(() => {
    if (appLanguages.length === 0 || appLanguages.some(lang => !lang)) {
      return null;
    }
    return /*#__PURE__*/_jsx(Section, {
      label: homemade ? unescapedText('appMarketplace.detail.overview.sidebar.details.homemadeAppLanguagesLabel') : unescapedText('appMarketplace.detail.overview.sidebar.details.appLanguagesLabel'),
      labelClassName: "m-bottom-1",
      children: /*#__PURE__*/_jsx(FormattedList, {
        children: appLanguages.map(languageCode => [languageCode, `SharedI18nStrings.languageNames.${languageCode}`]).sort(([__, languageNameI18nKey], [___, nextLanguageNameI18nKey]) => unescapedText(languageNameI18nKey).localeCompare(unescapedText(nextLanguageNameI18nKey))).map(([languageCode, languageNameI18nKey]) => /*#__PURE__*/_jsx(FormattedMessage, {
          message: languageNameI18nKey
        }, languageCode))
      })
    });
  }, [appLanguages, homemade]);
  const renderedCompanyName = useMemo(() => {
    return companyUrl ? /*#__PURE__*/_jsx(UILink, {
      external: true,
      href: getFormattedUrl(companyUrl) || undefined,
      onClick: () => trackInteraction('click provider link'),
      children: companyName
    }) : companyName;
  }, [companyName, companyUrl, trackInteraction]);
  const renderedInstallsSection = useMemo(() => {
    return /*#__PURE__*/_jsx(Section, {
      label: unescapedText('appMarketplace.detail.overview.sidebar.details.installsLabel'),
      children: formatInstalls(installs)
    });
  }, [installs]);
  return /*#__PURE__*/_jsxs(UIDescriptionList, {
    "data-test-id": "app-details",
    children: [/*#__PURE__*/_jsx(Section, {
      label: unescapedText('appMarketplace.detail.overview.sidebar.details.providerLabel'),
      children: renderedCompanyName
    }), renderedInstallsSection, isUngatedForAppRecategorization ? renderedCategoryV2Section : renderedCategorySection, !isUngatedForAppRecategorization && renderedFeaturesSection, renderedLanguagesSection]
  });
}